import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Curve3 } from '@babylonjs/core/Maths/math.path';

/**
 * Tangent frame representing coordinate system along a spline
 */
export interface TangentFrame {
  origin: Vector3;
  forward: Vector3;  // Tangent (along spline)
  right: Vector3;    // Perpendicular to tangent in XZ plane
  up: Vector3;       // World up (always Y axis)
}

/**
 * Compute tangent frame at a specific position on a spline
 * 
 * Coordinate system:
 * - Origin: Player position on spline
 * - Forward (Y): Spline tangent direction
 * - Right (Z): Perpendicular to tangent in XZ plane
 * - Up (X): World vertical (always +Y world direction)
 * 
 * @param spline The spline curve
 * @param position Player position (used to find closest point on spline)
 * @returns Tangent frame at the position
 */
export function computeTangentFrame(spline: Curve3, position: Vector3): TangentFrame {
  const points = spline.getPoints();
  
  // Find closest point on spline
  let closestIndex = 0;
  let closestDist = Number.MAX_VALUE;
  
  for (let i = 0; i < points.length; i++) {
    const dist = Vector3.DistanceSquared(points[i], position);
    if (dist < closestDist) {
      closestDist = dist;
      closestIndex = i;
    }
  }
  
  // Get tangent at closest point
  let tangent: Vector3;
  if (closestIndex === 0) {
    // First point: use vector to next point
    tangent = points[1].subtract(points[0]).normalize();
  } else if (closestIndex === points.length - 1) {
    // Last point: use vector from previous point
    tangent = points[closestIndex].subtract(points[closestIndex - 1]).normalize();
  } else {
    // Middle point: average of vectors to prev and next
    const toPrev = points[closestIndex].subtract(points[closestIndex - 1]).normalize();
    const toNext = points[closestIndex + 1].subtract(points[closestIndex]).normalize();
    tangent = toPrev.add(toNext).normalize();
  }
  
  // World up is always Y axis
  const up = Vector3.Up();
  
  // Right vector: cross product of tangent and world up
  // This gives us the perpendicular direction in the XZ plane
  const right = Vector3.Cross(tangent, up).normalize();
  
  // If tangent is perfectly vertical, right will be zero
  // In that case, use world right (X axis)
  if (right.lengthSquared() < 0.001) {
    return {
      origin: position.clone(),
      forward: tangent,
      right: Vector3.Right(),
      up
    };
  }
  
  return {
    origin: position.clone(),
    forward: tangent,
    right,
    up
  };
}

/**
 * Convert tangent-space offset to world-space position
 * 
 * Offset components:
 * - X: up/down (along world Y axis)
 * - Y: forward/back (along spline tangent)
 * - Z: left/right (perpendicular to spline in XZ plane)
 * 
 * @param frame The tangent frame
 * @param offset The tangent-space offset
 * @returns World-space position
 */
export function tangentToWorld(frame: TangentFrame, offset: Vector3): Vector3 {
  // X component = up/down (world Y)
  const upContribution = frame.up.scale(offset.x);
  
  // Y component = forward/back (tangent)
  const forwardContribution = frame.forward.scale(offset.y);
  
  // Z component = left/right (perpendicular)
  const rightContribution = frame.right.scale(offset.z);
  
  // Combine all contributions
  return frame.origin
    .add(upContribution)
    .add(forwardContribution)
    .add(rightContribution);
}

/**
 * Convert a world-space offset vector into tangent-space components.
 *
 * Tangent-space components:
 * - X: up/down (along world Y axis)
 * - Y: forward/back (along spline tangent)
 * - Z: left/right (perpendicular to spline in XZ plane)
 */
export function worldToTangent(frame: TangentFrame, worldOffset: Vector3): Vector3 {
  return new Vector3(
    Vector3.Dot(worldOffset, frame.up),
    Vector3.Dot(worldOffset, frame.forward),
    Vector3.Dot(worldOffset, frame.right)
  );
}
